/**
 * Multi-country bulk edit modal — 3-step wizard.
 * Step 1: Configuration, Step 2: Countries, Step 3: Creative.
 */

import { useCallback, useMemo, useState } from 'react';

import { ModalPortalSurfaceContext } from '@/components/ui';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import type { BulkUpdatePreparedDropsPayload } from '@/hooks';
import { buildBulkEditModalMergeState, buildMultiCountryPayloads } from './shared';
import { useMultiCountryBulkEditForm, useMultiCountryCreative } from './hooks';
import { ConfigStep, CountriesStep, CreativeStep } from './steps';
import { ModalHeader, ModalFooter } from './ModalHeaderFooter';
import { backdropClick } from '@/components/ui';

interface CatalogForCountry {
  gateways: Array<{ id: number; name: string; pricePerSms?: number }>;
  campaigns: Array<{ id: number; name: string }>;
  tlds: Array<{ value: string; displayName: string }>;
  leadsLists?: Array<{ id: number; name: string }>;
}

interface Props {
  selectedIds: string[];
  mergedDrops: PreparedDrop[];
  tlds: Array<{ value: string; displayName: string }>;
  catalogByCountry: Record<string, CatalogForCountry>;
  senderNames: string[];
  smsTexts: string[];
  onClose: () => void;
  onSave: (params: {
    countryKey: string;
    items: Array<{ id: string; wiserId: number }>;
    payload: BulkUpdatePreparedDropsPayload;
  }) => Promise<unknown>;
}

export function MultiCountryBulkEditModal(props: Props) {
  const { selectedIds, mergedDrops, tlds, catalogByCountry, senderNames, smsTexts, onClose, onSave } = props;
  const [isBusy, setIsBusy] = useState(false);
  const [portalSurface, setPortalSurface] = useState<HTMLDivElement | null>(null);

  const { countryKeys, sourceCountryKey } = useMemo(() => {
    const byId = new Map(mergedDrops.map((d) => [d.id, d]));
    const keys = new Set<string>();
    for (const id of selectedIds) {
      const d = byId.get(id);
      if (!d) continue;
      keys.add(d.countryKey);
    }
    const sorted = [...keys].sort();
    return { countryKeys: sorted, sourceCountryKey: sorted[0] ?? '' };
  }, [selectedIds, mergedDrops]);

  const form = useMultiCountryBulkEditForm({ selectedCountryKeys: countryKeys });

  const { mergedOrderedIds, dropCountByCountry, bulkEditTargets } = useMemo(
    () => buildBulkEditModalMergeState(selectedIds, form.propagateToCountries, mergedDrops, catalogByCountry),
    [selectedIds, form.propagateToCountries, mergedDrops, catalogByCountry],
  );

  const affectedDraftCount = mergedOrderedIds.length;

  const creative = useMultiCountryCreative({
    countryKeys: form.allCountryKeys,
    dropCountByCountry,
    globalCountryPerDrop: bulkEditTargets.globalCountryPerDrop,
    onTextsGenerated: form.saveGeneratedTextsByCountry,
    onSendersGenerated: form.saveGeneratedSendersByCountry,
    onTextsPerDropMergeOrder: form.saveGeneratedTextsPerDropMergeOrder,
    onSendersPerDropMergeOrder: form.saveGeneratedSendersPerDropMergeOrder,
    syncPerDropSmsTexts: form.saveGeneratedTextsPerDropMergeOrder,
    syncSmsTextsByCountry: form.saveGeneratedTextsByCountry,
  });

  const { flat: allDropLabels, byCountry: dropLabelsByCountry } = bulkEditTargets;

  const handleSave = useCallback(async () => {
    setIsBusy(true);
    try {
      const allIds = bulkEditTargets.orderedIds;
      const result = buildMultiCountryPayloads({ ids: allIds, mergedDrops, config: form.buildConfig() });
      if (result.type === 'grouped') {
        for (const p of result.payloads) await onSave(p);
      } else {
        for (const e of result.entries) {
          await onSave({ countryKey: e.countryKey, items: [{ id: e.id, wiserId: e.wiserId }], payload: e.payload });
        }
      }
      onClose();
    } finally {
      setIsBusy(false);
    }
  }, [form, mergedDrops, bulkEditTargets, onSave, onClose]);

  return (
    <ModalPortalSurfaceContext.Provider value={portalSurface}>
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 p-3 backdrop-blur-sm" onClick={backdropClick(onClose)}>
        <div
          ref={setPortalSurface}
          className="relative flex max-h-[94vh] w-[96vw] max-w-[900px] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <ModalHeader form={form} affectedDraftCount={affectedDraftCount} onClose={onClose} isBusy={isBusy} />
          <div className="min-h-0 flex-1 overflow-y-auto">
            {form.step === 1 && (
              <ConfigStep form={form} selectedIds={selectedIds} mergedDrops={mergedDrops} tlds={tlds} isBusy={isBusy} />
            )}
            {form.step === 2 && (
              <CountriesStep
                form={form} selectedIds={selectedIds} mergedDrops={mergedDrops}
                catalogByCountry={catalogByCountry} sourceCountryKey={sourceCountryKey}
                affectedDraftCount={affectedDraftCount} isBusy={isBusy}
              />
            )}
            {form.step === 3 && (
              <CreativeStep
                form={form} creative={creative} countryKeys={form.allCountryKeys}
                dropLabels={allDropLabels} dropLabelsByCountry={dropLabelsByCountry}
                sendersList={senderNames} textsList={smsTexts} isBusy={isBusy}
              />
            )}
          </div>
          <ModalFooter form={form} onSave={handleSave} onClose={onClose} isBusy={isBusy} />
        </div>
      </div>
    </ModalPortalSurfaceContext.Provider>
  );
}
